import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { REQUIRE_AUTH } from '../interceptors/auth-context';
import { AuthService } from '../../services/auth';
import { catchError, of } from 'rxjs';

interface OnboardingWelcomeStatusResponse {
  welcomeSeen: boolean;
}

@Injectable({ providedIn: 'root' })
export class OnboardingWelcomeService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private baseUrl = environment.apiUrl;

  private _showWelcome = signal(false);
  private _isChecking = signal(false);
  /** Whether the backend already recorded the welcome as seen */
  private _seen = signal(true);

  readonly showWelcome = this._showWelcome.asReadonly();
  readonly isChecking = this._isChecking.asReadonly();

  checkWelcomeStatus(): void {
    const userInfo = this.authService.getUserInfo();
    if (!userInfo?.business_id) return;

    // Only show for owner role
    if (!this.authService.isOwner()) return;

    // Skip for system admins
    if (this.authService.isSystemAdmin()) return;

    this._isChecking.set(true);

    this.http
      .get<OnboardingWelcomeStatusResponse>(
        `${this.baseUrl}/api/v1/businesses/${userInfo.business_id}/onboarding-welcome`,
        { context: new HttpContext().set(REQUIRE_AUTH, true) }
      )
      .pipe(catchError(() => of({ welcomeSeen: true })))
      .subscribe((status) => {
        this._isChecking.set(false);
        if (!status.welcomeSeen) {
          this._seen.set(false);
          this._showWelcome.set(true);
        }
      });
  }

  markAsSeen(): void {
    this._showWelcome.set(false);

    // Re-opened from the menu: nothing new to record
    if (this._seen()) return;

    const userInfo = this.authService.getUserInfo();
    if (!userInfo?.business_id) return;

    this._seen.set(true);

    this.http
      .post(
        `${this.baseUrl}/api/v1/businesses/${userInfo.business_id}/onboarding-welcome/seen`,
        {},
        { context: new HttpContext().set(REQUIRE_AUTH, true) }
      )
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  dismiss(): void {
    this.markAsSeen();
  }

  /** Re-open the welcome guide on demand (e.g. from the user menu) */
  open(): void {
    this._showWelcome.set(true);
  }
}
